import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { fileUrl } from "@/lib/api";

/**
 * Unified branded header used across the entire client-facing PWA flow
 * (Landing, Onboarding, Report, Sign, Practices, Profile).
 *
 * Shows the consultant's logo + studio name. Falls back to the GoStreet wordmark
 * only as a last resort when the consultant has not uploaded a logo.
 *
 * Props:
 *  - consultant: { logo_url?, studio_name?, primary_color? }
 *  - backTo?: route to navigate to when chevron-back is clicked. Hidden if null.
 *  - dark?: when true, uses dark navy background (matches /c/:slug landing)
 */
export default function ConsultantHeader({ consultant, backTo = null, dark = false }) {
  const navigate = useNavigate();
  const bg = dark ? "bg-[#0F172A] text-white" : "bg-white text-[#1E293B] border-b border-slate-200";
  const backBtnBg = dark ? "hover:bg-white/10 text-white/80" : "hover:bg-slate-100 text-slate-600";
  const studioColor = dark ? "text-white/70" : "text-slate-500";

  return (
    <header className={`${bg} safe-top sticky top-0 z-30`} data-testid="consultant-header">
      <div className="max-w-md mx-auto px-5 h-16 flex items-center gap-3">
        {backTo ? (
          <button
            onClick={() => navigate(backTo)}
            data-testid="consultant-header-back"
            className={`p-2 -ml-2 rounded-lg ${backBtnBg}`}
            aria-label="Indietro"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <div className="w-9" />
        )}

        <div className="flex-1 min-w-0 text-center flex flex-col items-center">
          {consultant?.logo_url ? (
            <img
              src={fileUrl(consultant.logo_url)}
              alt={consultant.studio_name || "Studio"}
              className="h-10 bg-white rounded-lg p-1 object-contain"
              style={{ maxWidth: 160 }}
              data-testid="consultant-header-logo"
            />
          ) : consultant?.studio_name ? (
            <div
              className="h-10 px-3 rounded-lg font-black flex items-center justify-center text-white"
              style={{ background: consultant.primary_color || "#2456C6" }}
              data-testid="consultant-header-logo-fallback"
            >
              {consultant.studio_name}
            </div>
          ) : (
            <Logo className="h-9" />
          )}
          {consultant?.studio_name && consultant?.logo_url && (
            <div className={`text-[11px] ${studioColor} mt-1 truncate max-w-full font-bold uppercase tracking-wider`} data-testid="consultant-header-studio">
              {consultant.studio_name}
            </div>
          )}
        </div>

        <div className="w-9" />
      </div>
    </header>
  );
}
